import Button from '@rio-cloud/rio-uikit/Button';
import Rioglyph, { type IconType } from '@rio-cloud/rio-uikit/Rioglyph';
import TabbedPanel, { type TabbedPanelTab } from '@rio-cloud/rio-uikit/TabbedPanel';
import { Component, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { MessageKey } from '../../../../configuration';
import { UnreachableCaseError } from '../../../../utils/typescriptUtil';
import { PaymentMethodType } from '../../api/paymentMethods/paymentMethodTypes.types';
import { safeDataLayerPush } from '../utils/googleTagManagerWrapper';
import { BankTransferPayment } from './BankTransferPayment';
import { PaymentInformation } from './PaymentInformation';
import { getGAPaymentType } from './paymentMethodsGTMUtils';
import type { PaymentMethod } from './redux/types';
import { StripePaymentFormContainer } from './StripePaymentFormContainer';

function pushToGA({ paymentType }: { paymentType?: PaymentMethodType } = {}) {
    if (paymentType) {
        const createEvent = () => ({
            event: 'PaymentMethodSelected',
            eventPayload: {
                paymentType: getGAPaymentType(paymentType),
            },
        });
        safeDataLayerPush(createEvent);
    }
}

interface Props {
    paymentInformation?: PaymentMethod;
    paymentMethodTypes: PaymentMethodType[];
    setEditMode: (isEditMode: boolean) => void;
    isEditPayment: boolean;
}

type OwnState = { paymentMethodType?: PaymentMethodType; isSubmittingToStripe: boolean };

export class PaymentMethodPage extends Component<Props, OwnState> {
    constructor(props: Props) {
        super(props);
        if (props.paymentInformation) {
            this.state = {
                paymentMethodType: props.paymentInformation.paymentType,
                isSubmittingToStripe: false,
            };
        } else {
            this.state = {
                paymentMethodType: PaymentMethodType.CREDIT_CARD,
                isSubmittingToStripe: false,
            };
            this.props.setEditMode(true);
        }
        this.setSubmittingToStripe = this.setSubmittingToStripe.bind(this);
    }

    componentWillUnmount() {
        this.props.setEditMode(false);
    }

    setSubmittingToStripe(isSubmittingToStripe: boolean) {
        this.setState({ isSubmittingToStripe });
    }

    setPaymentMethodType(paymentMethodType: PaymentMethodType) {
        pushToGA({ paymentType: paymentMethodType });
        this.setState({ paymentMethodType });
    }

    handleShowCurrentPaymentMethod() {
        this.props.setEditMode(false);
        this.setState({
            paymentMethodType: this.props.paymentInformation?.paymentType,
        });
    }

    renderPaymentForm(paymentMethod?: PaymentMethodType, paymentInformation?: PaymentMethod) {
        if (paymentMethod === PaymentMethodType.BANK_TRANSFER) {
            return (
                <div className='max-width-700'>
                    <BankTransferPayment />
                </div>
            );
        }
        return (
            <div className='max-width-700'>
                <StripePaymentFormContainer
                    paymentMethod={paymentMethod}
                    paymentInformation={paymentInformation}
                    setSubmittingToStripe={this.setSubmittingToStripe}
                />
            </div>
        );
    }

    renderPaymentInformation(paymentInformation?: PaymentMethod) {
        const icon = this.getPaymentMethodIcon(paymentInformation?.paymentType);

        return (
            <div className='width-100pct max-width-700 text-center padding-y-25'>
                <div className='width-70 height-70 rounded-circle bg-highlight-decent display-flex align-items-center justify-content-center margin-x-auto margin-bottom-15'>
                    <Rioglyph icon={icon} size='h2' iconClassName='text-color-highlight' />
                </div>
                <h4 className='margin-top-0 margin-bottom-15'>
                    <FormattedMessage id='marketplace.payment.information.header.current' />
                </h4>
                <PaymentInformation paymentInformation={paymentInformation} />
            </div>
        );
    }

    getPaymentMethodIcon(paymentMethodType?: PaymentMethodType): IconType {
        if (paymentMethodType === PaymentMethodType.CREDIT_CARD) {
            return 'rioglyph-card-reader';
        }
        if (paymentMethodType === PaymentMethodType.SEPA) {
            return 'rioglyph-building';
        }
        return 'rioglyph-document';
    }

    renderPaymentTabs({
        paymentMethodType,
        disableNoneActiveTabs,
        paymentMethodTypes,
        paymentContent,
    }: {
        paymentMethodType?: PaymentMethodType;
        disableNoneActiveTabs: boolean;
        paymentMethodTypes: PaymentMethodType[];
        paymentContent: ReactNode;
    }) {
        const tabs: TabbedPanelTab[] = paymentMethodTypes.map(type => {
            let headerId: MessageKey;
            let icon: IconType;

            switch (type) {
                case PaymentMethodType.SEPA:
                    headerId = 'marketplace.payment.method.sepa';
                    icon = 'rioglyph-building';
                    break;
                case PaymentMethodType.CREDIT_CARD:
                    headerId = 'marketplace.payment.method.creditCard';
                    icon = 'rioglyph-card-reader';
                    break;
                case PaymentMethodType.BANK_TRANSFER:
                    headerId = 'marketplace.payment.method.bankTransfer';
                    icon = 'rioglyph-document';
                    break;
                default:
                    throw new UnreachableCaseError(type);
            }

            const isActive = paymentMethodType === type;

            return {
                key: type,
                disabled: disableNoneActiveTabs && !isActive,
                title: (
                    <span
                        className={`display-flex align-items-center gap-10${isActive ? ' text-color-highlight' : ''}`}
                    >
                        <Rioglyph icon={icon} size='h4' />
                        <FormattedMessage id={headerId} />
                    </span>
                ),
                content: paymentContent,
            };
        });

        return (
            <TabbedPanel
                tabs={tabs}
                activeKey={paymentMethodType}
                onSelectTab={tabKey => this.setPaymentMethodType(tabKey as PaymentMethodType)}
                bsStyle='blank'
                bodyTransition='none'
                className='margin-0'
                headerClassName='flex-wrap border-bottom-none'
                navClassName='flex-wrap flex-1-1'
                bodyClassName='padding-0'
                activeContentClassName='display-flex flex-column align-items-center padding-top-25'
                headerRight={
                    this.props.isEditPayment ? this.renderShowCurrentPaymentButton() : this.renderEditPaymentButton()
                }
            />
        );
    }

    renderEditPaymentButton() {
        return (
            <Button
                key='editPayment'
                className='changePayment'
                iconName='rioglyph-pencil'
                onClick={() => this.props.setEditMode(true)}
                autoTrackingKey='marketplace.payment.button.changePayment'
            >
                <FormattedMessage id='marketplace.payment.button.changePayment' />
            </Button>
        );
    }

    renderShowCurrentPaymentButton() {
        if (!this.props.paymentInformation) {
            return null;
        }
        const disabled = this.state.isSubmittingToStripe;

        return (
            <Button
                key='showCurrentPayment'
                className='showCurrentPayment'
                disabled={disabled}
                iconName='rioglyph-revert'
                onClick={() => this.handleShowCurrentPaymentMethod()}
                autoTrackingKey='marketplace.payment.button.showCurrentPayment'
            >
                <FormattedMessage id='marketplace.payment.button.showCurrentPayment' />
            </Button>
        );
    }

    renderPaymentMethodPageBody({
        paymentInformation,
        paymentMethodType,
        paymentMethodTypes,
    }: Pick<Props, 'paymentInformation' | 'paymentMethodTypes'> & { paymentMethodType?: PaymentMethodType }) {
        const paymentContent = this.props.isEditPayment
            ? this.renderPaymentForm(paymentMethodType, paymentInformation)
            : this.renderPaymentInformation(paymentInformation);
        const disableNoneActiveTabs = !!paymentInformation && !this.props.isEditPayment;

        return (
            <div>
                {this.renderPaymentTabs({
                    paymentMethodType,
                    disableNoneActiveTabs,
                    paymentMethodTypes,
                    paymentContent,
                })}
            </div>
        );
    }

    render() {
        return (
            <div className='PaymentMethodPage'>
                {this.renderPaymentMethodPageBody({
                    paymentInformation: this.props.paymentInformation,
                    paymentMethodType: this.state.paymentMethodType,
                    paymentMethodTypes: this.props.paymentMethodTypes,
                })}
            </div>
        );
    }
}
